/** Header user menu — identity + logout. Hidden when auth is disabled. */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LogOut, UserRound } from "lucide-react";

import { useAuth } from "@/auth/AuthContext";

export function UserMenu() {
  const { t } = useTranslation();
  const { authEnabled, user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!authEnabled || !user) return null;

  const label = user.full_name || user.email;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-border px-2.5 text-sm text-fg-muted hover:bg-bg-subtle"
      >
        <UserRound aria-hidden="true" className="h-4 w-4" />
        <span className="hidden max-w-[10rem] truncate sm:inline">{label}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 z-30 mt-2 w-56 rounded-md border border-border bg-bg-elevated p-2 shadow-lg"
        >
          <div className="border-b border-border px-2 pb-2">
            <p className="truncate text-sm font-medium text-fg">{label}</p>
            <p className="truncate text-xs text-fg-subtle">{user.email}</p>
            {user.roles.length > 0 && (
              <p className="mt-1 text-[11px] text-fg-subtle">{user.roles.join(", ")}</p>
            )}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
            className="mt-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-fg-muted hover:bg-bg-subtle hover:text-fg"
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
            {t("auth.logout")}
          </button>
        </div>
      )}
    </div>
  );
}
